/**
 * The four categories, as the screens word them.
 *
 * The words and the order are the design's, read off the running export: Rules, Flows, Guides,
 * Knowledge, with the editor's one-line explanation of each beside it. The values are
 * `NoteCategory` from `apps/handbook-mcp/app/schemas/workspace.py` and nothing else — the earlier
 * export drew `skill` and `agent` too, and neither is a category the service accepts.
 *
 * Kept out of the components because three screens word them identically: the sidebar groups by
 * them, the list badges a row with one, and the editor picks one.
 */

import type { NoteCategory, NoteGrade } from "../api/types";

export interface CategoryMeta {
  value: NoteCategory;
  /** The sidebar group and the list heading. */
  plural: string;
  /** The uppercase badge on a list row. */
  word: string;
  /** The editor's option text, verbatim from the design. */
  option: string;
}

export const CATEGORIES: readonly CategoryMeta[] = [
  { value: "rule", plural: "Rules", word: "Rule", option: "Rule — how we do a thing" },
  { value: "flow", plural: "Flows", word: "Flow", option: "Flow — steps in order" },
  { value: "guide", plural: "Guides", word: "Guide", option: "Guide — longer explanation" },
  {
    value: "knowledge",
    plural: "Knowledge",
    word: "Knowledge",
    option: "Knowledge — deep research on a topic",
  },
] as const;

const BY_VALUE = new Map<NoteCategory, CategoryMeta>(CATEGORIES.map((meta) => [meta.value, meta]));

export function categoryMeta(value: NoteCategory): CategoryMeta {
  // Every `NoteCategory` is in the table above, so this fallback is unreachable through the types.
  // It exists because the value arrives over the wire, and a category added server-side should
  // render as itself rather than crash a list.
  return BY_VALUE.get(value) ?? { value, plural: value, word: value, option: value };
}

/** Narrow an unknown string — a URL search parameter — to a category, or to nothing. */
export function asCategory(value: unknown): NoteCategory | undefined {
  return typeof value === "string" && BY_VALUE.has(value as NoteCategory)
    ? (value as NoteCategory)
    : undefined;
}

/**
 * `NoteGrade`, strongest first.
 *
 * Only a rule carries one. `_Categorised._grade_belongs_to_a_rule` rejects the combination
 * outright, so the editor shows this control on a rule and on nothing else.
 */
export const GRADES: readonly NoteGrade[] = [
  "MUST",
  "MUST NOT",
  "SHOULD",
  "SHOULD NOT",
  "MAY",
] as const;

export function asGrade(value: unknown): NoteGrade | undefined {
  return typeof value === "string" && (GRADES as readonly string[]).includes(value)
    ? (value as NoteGrade)
    : undefined;
}
